/**
 * Notifications API Client
 *
 * REST API client for notification operations:
 * - Get notifications list
 * - Mark single notification as read
 * - Mark all notifications as read
 *
 * Uses existing API infrastructure:
 * - buildApiUrl() for URL construction
 * - fetchWithTokenRefresh() for authenticated requests
 * - handleApiResponse() for response parsing
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api/config';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';
import type { GetNotificationsResponse, MarkAsReadResponse } from '../types';

/**
 * Parameters for getNotifications request
 */
interface GetNotificationsParams {
  limit?: number;
  page?: number;
  unreadOnly?: boolean;
  excludeTypes?: string[];
}

/**
 * Get list of notifications
 *
 * @param params - Query parameters
 * @returns Promise with notifications list and metadata
 *
 * @example
 * const response = await getNotifications({ limit: 10 });
 * console.log(response.data); // Array of notifications
 * console.log(response.meta.hasMore); // true if more available
 */
export async function getNotifications(
  params: GetNotificationsParams = {}
): Promise<GetNotificationsResponse> {
  const { limit = 10, page = 1, unreadOnly = false, excludeTypes } = params;

  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    page: page.toString(),
    ...(unreadOnly && { unreadOnly: 'true' }),
  });

  excludeTypes?.forEach((type) => queryParams.append('excludeTypes', type));

  const url = buildApiUrl(`/notifications?${queryParams}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
  });

  return handleApiResponse<GetNotificationsResponse>(response);
}

/**
 * Mark single notification as read
 *
 * @param notificationId - ID of the notification to mark as read
 * @returns Promise with success response
 *
 * @example
 * await markNotificationAsRead('abc-123-def-456');
 */
export async function markNotificationAsRead(
  notificationId: string
): Promise<MarkAsReadResponse> {
  const url = buildApiUrl(`/notifications/${notificationId}/read`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
  });

  return handleApiResponse<MarkAsReadResponse>(response);
}

/**
 * Mark all notifications as read
 *
 * @returns Promise with success response
 *
 * @example
 * await markAllNotificationsAsRead();
 */
export async function markAllNotificationsAsRead(): Promise<MarkAsReadResponse> {
  const url = buildApiUrl('/notifications/mark-all-read');

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
  });

  return handleApiResponse<MarkAsReadResponse>(response);
}

export interface NotificationPreferences {
  id: string;
  userId: string;
  // Email channel
  emailEnabled: boolean;
  emailMarketing: boolean;
  // Push channel
  pushEnabled: boolean;
  // In-app channel
  inAppEnabled: boolean;
  // UI suppression flags
  suppressOrderArchiveWarning: boolean;
  updatedAt: string;
}

export type UpdateNotificationPreferencesPayload = Partial<
  Pick<
    NotificationPreferences,
    'emailEnabled' | 'emailMarketing' | 'pushEnabled' | 'inAppEnabled' | 'suppressOrderArchiveWarning'
  >
>;

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  const url = buildApiUrl('/notifications/preferences');
  const response = await fetchWithTokenRefresh(url, { method: 'GET' });
  return handleApiResponse<NotificationPreferences>(response);
}

export async function updateNotificationPreferences(
  payload: UpdateNotificationPreferencesPayload
): Promise<NotificationPreferences> {
  const url = buildApiUrl('/notifications/preferences');
  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleApiResponse<NotificationPreferences>(response);
}
